import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { FiPaperclip, FiX, FiDownload, FiAlertTriangle } from 'react-icons/fi';
import { QBASE_API } from '../config/qbase';

// 퀴즈 첨부 파일은 QBase 가 들고 있다 (LiveSQL 테넌트 편입 1단계, 2026-08-19).
// 예전에는 Node 백엔드가 서비스계정으로 대신 받아다 줬지만, 이제 브라우저가 자기 세션으로
// 직접 받는다 — withCredentials 가 필요한 이유다(첨부도 로그인한 사람만 볼 수 있어야 한다).
const axiosInstance = axios.create({
    baseURL: `${QBASE_API}/livesql`,
    timeout: 30000,
    withCredentials: true,
    validateStatus: function (status) {
        return status < 500;
    }
});

const AttachmentModal = ({ attachmentList, challengeId, quizNo, onClose }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(0);
    const [fileContents, setFileContents] = useState({});
    const [loading, setLoading] = useState({});
    const [errors, setErrors] = useState({});

    // 첨부파일 목록 파싱
    const attachments = React.useMemo(() => {
        if (!attachmentList) return [];
        try {
            const parsed = typeof attachmentList === 'string' ? JSON.parse(attachmentList) : attachmentList;
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            console.error('첨부파일 목록 파싱 오류:', error);
            return [];
        }
    }, [attachmentList]);

    // 파일 확장자로 타입 판단
    const getFileType = (filename) => {
        const ext = filename.split('.').pop().toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) {
            return 'image';
        } else if (ext === 'md') {
            return 'markdown';
        } else if (['txt', 'sql', 'js', 'json', 'xml', 'html', 'css', 'csv'].includes(ext)) {
            return 'text';
        } else {
            return 'binary';
        }
    };

    // 파일 내용 가져오기
    const fetchFileContent = async (file, index) => {
        if (fileContents[index]) return; // 이미 로드됨

        setLoading(prev => ({ ...prev, [index]: true }));
        setErrors(prev => ({ ...prev, [index]: null }));

        try {
            const filePath = file.filename;
            const fileType = getFileType(file.filename);

            if (fileType === 'image') {
                // 이미지 파일 - Blob으로 가져와서 URL 생성
                const response = await axiosInstance.get(`/downloadQuizFile`, {
                    params: {
                        challengeId,
                        quizNo,
                        filename: filePath
                    },
                    responseType: 'blob'
                });
                const blob = response.data;
                const url = window.URL.createObjectURL(blob);
                setFileContents(prev => ({ ...prev, [index]: { type: 'image', url } }));
            } else if (fileType === 'markdown') {
                // 마크다운 파일 - 텍스트로 가져오기
                const response = await axiosInstance.get(`/downloadQuizFile`, {
                    params: {
                        challengeId,
                        quizNo,
                        filename: filePath
                    },
                    responseType: 'text'
                });
                setFileContents(prev => ({ ...prev, [index]: { type: 'markdown', content: response.data } }));
            } else if (fileType === 'text') {
                // 텍스트 파일 - 텍스트로 가져오기
                const response = await axiosInstance.get(`/downloadQuizFile`, {
                    params: {
                        challengeId,
                        quizNo,
                        filename: filePath
                    },
                    responseType: 'text'
                });
                setFileContents(prev => ({ ...prev, [index]: { type: 'text', content: response.data } }));
            } else {
                // 기타 바이너리 파일 - 다운로드만 지원
                setFileContents(prev => ({ ...prev, [index]: { type: 'binary', filePath } }));
            }
        } catch (error) {
            console.error('파일 로드 오류:', error);
            const errorMessage = error.response?.data?.error || error.message || t('common:att.loadFailed');
            setErrors(prev => ({ ...prev, [index]: errorMessage }));
        } finally {
            setLoading(prev => ({ ...prev, [index]: false }));
        }
    };

    // 활성 탭 변경 시 파일 로드
    useEffect(() => {
        if (attachments.length > 0 && activeTab < attachments.length) {
            fetchFileContent(attachments[activeTab], activeTab);
        }
    }, [activeTab, attachments]);

    // 파일 다운로드 처리
    const handleDownload = (file) => {
        const params = new URLSearchParams({
            challengeId,
            quizNo,
            filename: file.filename
        });
        // 브라우저 내비게이션이라 같은 오리진이어야 세션 쿠키가 실린다 (nginx / setupProxy 가 보장)
        window.location.href = `${QBASE_API}/livesql/downloadQuizFile?${params.toString()}`;
    };

    if (attachments.length === 0) {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    background: '#fff',
                    borderRadius: 8,
                    padding: 24,
                    maxWidth: 400,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}>
                    <h3 style={{ margin: 0, marginBottom: 16, fontSize: 16, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <FiPaperclip size={14} />{t('common:att.title')}
                    </h3>
                    <p style={{ margin: 0, marginBottom: 16, fontSize: 13, color: '#666' }}>
                        {t('common:att.empty')}
                    </p>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '8px 16px',
                            background: '#007bff',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 12
                        }}
                    >
                        {t('common:att.close')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: '#fff',
                    borderRadius: 8,
                    width: '80%',
                    maxWidth: 900,
                    height: '80%',
                    maxHeight: 700,
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    overflow: 'hidden'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* 헤더 */}
                <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: '#f8f9fa'
                }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <FiPaperclip size={14} />{t('common:att.titleCount', { n: attachments.length })}
                    </h3>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#666',
                            padding: '4px 8px',
                            display: 'inline-flex',
                            alignItems: 'center',
                        }}
                    >
                        <FiX size={18} />
                    </button>
                </div>

                {/* 탭 헤더 */}
                <div style={{
                    display: 'flex',
                    borderBottom: '1px solid #e0e0e0',
                    background: '#fff',
                    overflowX: 'auto'
                }}>
                    {attachments.map((file, index) => (
                        <button
                            key={index}
                            onClick={() => setActiveTab(index)}
                            style={{
                                padding: '12px 20px',
                                border: 'none',
                                background: activeTab === index ? '#fff' : '#f8f9fa',
                                color: activeTab === index ? '#007bff' : '#666',
                                fontWeight: activeTab === index ? 'bold' : 'normal',
                                cursor: 'pointer',
                                fontSize: 12,
                                borderBottom: activeTab === index ? '2px solid #007bff' : '2px solid transparent',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {file.title || file.filename || t('common:att.fileN', { n: index + 1 })}
                        </button>
                    ))}
                </div>

                {/* 탭 내용 */}
                <div style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: 16,
                    background: '#fafafa'
                }}>
                    {loading[activeTab] ? (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: '#666',
                            fontSize: 14
                        }}>
                            <div>{t('common:att.loading')}</div>
                        </div>
                    ) : errors[activeTab] ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: '#f44336',
                            fontSize: 14
                        }}>
                            <div style={{ marginBottom: 16, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <FiAlertTriangle size={14} />{errors[activeTab]}
                            </div>
                            <button
                                onClick={() => {
                                    setErrors(prev => ({ ...prev, [activeTab]: null }));
                                    fetchFileContent(attachments[activeTab], activeTab);
                                }}
                                style={{
                                    padding: '8px 16px',
                                    background: '#007bff',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    fontSize: 12
                                }}
                            >
                                {t('common:att.retry')}
                            </button>
                        </div>
                    ) : fileContents[activeTab] ? (
                        <div>
                            {fileContents[activeTab].type === 'image' && (
                                <div style={{ textAlign: 'center' }}>
                                    <img
                                        src={fileContents[activeTab].url}
                                        alt={attachments[activeTab].filename}
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain'
                                        }}
                                    />
                                </div>
                            )}
                            {fileContents[activeTab].type === 'markdown' && (
                                <div style={{
                                    background: '#fff',
                                    border: '1px solid #ddd',
                                    borderRadius: 4,
                                    padding: 16,
                                    fontSize: 14,
                                    lineHeight: 1.6,
                                    overflow: 'auto'
                                }}>
                                    <ReactMarkdown>{fileContents[activeTab].content}</ReactMarkdown>
                                </div>
                            )}
                            {fileContents[activeTab].type === 'text' && (
                                <pre style={{
                                    background: '#fff',
                                    border: '1px solid #ddd',
                                    borderRadius: 4,
                                    padding: 16,
                                    fontSize: 12,
                                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                    whiteSpace: 'pre-wrap',
                                    wordWrap: 'break-word',
                                    margin: 0
                                }}>
                                    {fileContents[activeTab].content}
                                </pre>
                            )}
                            {fileContents[activeTab].type === 'binary' && (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    color: '#666',
                                    fontSize: 14
                                }}>
                                    <div style={{ marginBottom: 16 }}>
                                        📄 {attachments[activeTab].filename}
                                    </div>
                                    <div style={{ marginBottom: 16, fontSize: 12, color: '#999' }}>
                                        {t('common:att.noPreview')}
                                    </div>
                                    <button
                                        onClick={() => handleDownload(attachments[activeTab])}
                                        style={{
                                            padding: '8px 16px',
                                            background: '#007bff',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: 4,
                                            cursor: 'pointer',
                                            fontSize: 12
                                        }}
                                    >
                                        {t('common:att.download')}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: '#999',
                            fontSize: 14
                        }}>
                            {t('common:att.loadingFile')}
                        </div>
                    )}
                </div>

                {/* 다운로드 버튼 영역 */}
                {fileContents[activeTab] && fileContents[activeTab].type !== 'binary' && (
                    <div style={{
                        padding: 12,
                        borderTop: '1px solid #e0e0e0',
                        background: '#f8f9fa',
                        textAlign: 'right'
                    }}>
                        <button
                            onClick={() => handleDownload(attachments[activeTab])}
                            style={{
                                padding: '8px 16px',
                                background: '#28a745',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                fontSize: 12,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                            }}
                        >
                            <FiDownload size={12} />{t('common:att.download')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AttachmentModal;
